import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromHome from '../../state';
import * as fromRoot from '../../../state';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PostState } from '../../state/states/post.state';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EchoDialogComponent } from '../echo-dialog/echo-dialog.component';
import { MarkdownService } from '../../../markdown/markdown.service';
import { VotePostInput } from '../../../shared/data/inputs/vote-post.input';
import { PostInput } from '../../../shared/data/inputs/post.input';
import { NewPostMutationInput } from '../../../shared/data/inputs/new-post-mutation.input';
import { CastPollVoteInput } from '../../../shared/data/inputs/cast-poll-vote.input';
import { CreateCommentMutationInput } from '../../../shared/data/inputs/create-comment-mutation.input';
import { VoteCommentInput } from '../../../shared/data/inputs/vote-comment.input';
import { GetPostCommentsQueryInput } from '../../../shared/data/inputs/get-post-comments-query.input';
import { CommentPostInput } from '../../../shared/data/inputs/comment-post.input';
import { CommentScrollInput } from '../../../shared/data/inputs/comment-scroll.input';
import { PostBodyModel } from '../../../shared/data/models/post-body.model';
import { MiniUserModel } from '../../../shared/data/models/mini-user.model';
import { HttpClient } from '@angular/common/http';
import {
  delay,
  distinctUntilChanged,
  skipWhile,
  takeUntil
} from 'rxjs/operators';
import { GetCommentsRepliesInput } from '../../../shared/data/inputs/get-comments-replies.input';
import { Lightbox } from 'ngx-lightbox';
import { CommentReplyEventInput } from '../../state/inputs/comment-reply-event.input';
import { CreateReplyMessageInput } from '../../state/inputs/create-reply-message.input';
import { PostCommentReplyInput } from '../../state/inputs/post-comment-reply.input';
import { ReportDialogComponent } from '../report-dialog/report-dialog.component';
import { Giphy } from '../giphy/giphy.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { duration } from 'moment';

@Component({
  selector: 'anms-post',
  templateUrl: './post.component.html',
  styleUrls: ['./post.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PostComponent implements OnInit, OnDestroy {
  @Input() postState: PostState;
  @Input() hideActionButtons: Boolean = false;
  @Input() navigateShowComments: Boolean = false;

  // single post operations
  @Output() upVotePostEvent = new EventEmitter<VotePostInput>();
  @Output() downVotePostEvent = new EventEmitter<VotePostInput>();
  @Output() unVotePostEvent = new EventEmitter<VotePostInput>();
  @Output() deletePostEvent = new EventEmitter<PostInput>();
  @Output() favouritePostEvent = new EventEmitter<PostInput>();
  @Output() unFavouritePostEvent = new EventEmitter<PostInput>();
  @Output() echoPostEvent = new EventEmitter<NewPostMutationInput>();

  @Output() castForPollEvent = new EventEmitter<CastPollVoteInput>();

  // comment outputs
  @Output() createCommentEvent = new EventEmitter<CreateCommentMutationInput>();
  @Output() upVoteCommentEvent = new EventEmitter<VoteCommentInput>();
  @Output() downVoteCommentEvent = new EventEmitter<VoteCommentInput>();
  @Output() showCommentsEvent = new EventEmitter<GetPostCommentsQueryInput>();
  @Output() hideCommentsEvent = new EventEmitter<string>();
  @Output() loadMoreCommentsEvent = new EventEmitter<
    GetPostCommentsQueryInput
  >();
  @Output() deleteCommentEvent = new EventEmitter<CommentPostInput>();

  @Output() createCommentReplyEvent = new EventEmitter<
    CreateReplyMessageInput
  >();

  // comments scroll event
  // postID
  @Output() scrollCommentsEvent = new EventEmitter<CommentScrollInput>();

  // replay comment event.
  @Output() showReplyCommentEvent = new EventEmitter<any>();

  @Output() hideReplyCommentsEvent = new EventEmitter<PostCommentReplyInput>();
  @Output() loadMoreReplyCommentsEvent = new EventEmitter<
    PostCommentReplyInput
  >();

  @Output() upVoteReplyCommentEvent = new EventEmitter<PostCommentReplyInput>();
  @Output() downVoteReplyCommentEvent = new EventEmitter<
    PostCommentReplyInput
  >();
  @Output() deleteReplyCommentEvent = new EventEmitter<PostCommentReplyInput>();

  @Output() selectedGiphyEvent = new EventEmitter<Giphy>();

  commentGroup: FormGroup;

  currentUser$: Observable<MiniUserModel>;

  unSubscribed$ = new Subject();

  lastUrl: string;

  private _albums = [];

  showProfileCardEvent$ = new BehaviorSubject<boolean>(false);

  @ViewChild('gifEmojiTrigger') emojiTrigger;

  sets = [
    'native',
    'google',
    'twitter',
    'facebook',
    'emojione',
    'apple',
    'messenger'
  ];
  set = 'twitter';

  emojiEvent = new Subject<string>();

  constructor(
    private store$: Store<fromHome.State>,
    private _formBuilder: FormBuilder,
    private router: Router,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private markdownService: MarkdownService,
    private http: HttpClient,
    private _lightbox: Lightbox,
    private window: Window,
    private refChangeDetector: ChangeDetectorRef,
    private _snackBar: MatSnackBar
  ) {
    this.commentGroup = this.createCommentGroup();
  }
  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000
    });
  }

  ngOnInit(): void {
    this.currentUser$ = this.store$.select(fromRoot.getMe);

    this.markdownService
      .extractLastUrl(this.postState?.post?.body)
      .pipe(
        takeUntil(this.unSubscribed$),
        skipWhile(ur => ur === '')
      )
      .subscribe(lastUrl => {
        this.lastUrl = lastUrl;
      });
  }

  showProfileCard(): void {
    this.hideProfileCard();
    this.showProfileCardEvent$
      .pipe(delay(200), distinctUntilChanged())
      .subscribe(_ => {
        this.showProfileCardEvent$.next(true);
      });
  }

  hideProfileCard(): void {
    this.showProfileCardEvent$.next(false);
  }

  parseBody(body): PostBodyModel {
    let data: any;
    try {
      const parsedbody = JSON.parse(body) as PostBodyModel;

      data = {
        ...parsedbody,
        body: this.markdownService.convertMarkDownForDisplay(parsedbody.body)
      } as PostBodyModel;

      this._albums =
        parsedbody?.images.map(url => {
          return {
            src: url
            // caption: 'post images',
            // thumb: url
          };
        }) || [];
    } catch (e) {
      data = {
        body: this.markdownService.convertMarkDownForDisplay(body)
      };

      this._albums =
        this.postState?.post?.images?.map(url => {
          return {
            src: url.url
            // caption: 'post images',
            // thumb: ',/assets/images/profile.jpg'
          };
        }) || [];
    }
    return data;
  }

  open(index: number): void {
    this._lightbox.open(this._albums, index);
  }

  scrollComments(): void {
    const comments = this.postState.commentsState || [];
    const commentLength = comments.length;

    if (commentLength > 0) {
      this.scrollCommentsEvent.emit({
        postId: this.postState.post.Id,
        commentId: this.postState.commentsState[commentLength - 1].comment.Id
      });
    }
  }

  createCommentGroup(): FormGroup {
    return this._formBuilder.group({
      comment: ['', Validators.compose([Validators.required])]
    });
  }

  createComment(): void {
    const comment: CreateCommentMutationInput = {
      comment: this.markdownService.convert(
        this.commentGroup.getRawValue().comment
      ),
      postID: this.postState.post.Id
    };
    this.createCommentEvent.emit(comment);
    this.commentGroup.reset();
  }

  upVotePost(): void {
    const input: VotePostInput = {
      postID: this.postState.post.Id
    };
    this.upVotePostEvent.emit(input);
  }

  downVotePost(): void {
    const input: VotePostInput = {
      postID: this.postState.post.Id
    };
    this.downVotePostEvent.emit(input);
  }

  echoPost(): void {
    const dialogRef = this.dialog.open(EchoDialogComponent, {
      panelClass: 'echo-dialog',
      width: '600px',
      height: 'auto',
      data: {
        echoContent: this.postState?.post?.echoContent,
        post: this.postState?.post
      }
    });

    dialogRef.afterClosed().subscribe((data: NewPostMutationInput) => {
      if (!data) {
        return;
      }
      this.echoPostEvent.emit(data);
    });
  }

  echoWithoutComment(): void {
    // const dialogref = this.dialog.open(ConfirmationDialogComponent, {
    //   data: {
    //     title: `Rescribe`,
    //     message: `Are you sure you want to Rescribe this post by @${this.postState?.post?.author?.handle}`
    //   }
    // });
    //
    // dialogref.afterClosed().subscribe(res => {
    //   if (!res) return;
    //
    //   const content: NewPostMutationInput = {
    //     postType: 'ECHO',
    //     echoData: {
    //       postID:
    //         this.postState?.post?.echoContent?.originalPost?.Id ||
    //         this.postState?.post?.Id
    //     }
    //   };
    //
    //   this.echoPostEvent.emit(content);
    // });

    const content: NewPostMutationInput = {
      postType: 'ECHO',
      echoData: {
        postID:
          this.postState?.post?.echoContent?.originalPost?.Id ||
          this.postState?.post?.Id
      }
    };

    this.echoPostEvent.emit(content);
  }

  reportPost(): void {
    const dialogref = this.dialog.open(ReportDialogComponent, {
      data: {
        title: `Report Post`,
        message: `Describe the reason you are reporting this post.`
      }
    });
  }

  upVoteComment(commentID: string): void {
    const input: VoteCommentInput = {
      commentID,
      postID: this.postState.post.Id
    };
    this.upVoteCommentEvent.emit(input);
  }

  downVoteComment(commentID: string): void {
    const input: VoteCommentInput = {
      commentID,
      postID: this.postState.post.Id
    };
    this.downVoteCommentEvent.emit(input);
  }

  showComments(): void {
    const input: GetPostCommentsQueryInput = {
      postID: this.postState.post.Id,
      commentLimit: 3
    };
    this.showCommentsEvent.emit(input);
  }

  showReplyComment(commentID: string): void {
    const input: {
      commentID: string;
      postID: string;
      input: GetCommentsRepliesInput;
    } = {
      commentID: commentID,
      postID: this.postState.post.Id,
      input: {
        limit: 5,
        parentCommentID: commentID
      }
    };
    this.showReplyCommentEvent.emit(input);
  }

  hideComments(): void {
    this.hideCommentsEvent.emit(this.postState.post.Id);
  }

  loadMoreComments(): void {
    const input: GetPostCommentsQueryInput = {
      commentLimit: 5,
      postID: this.postState.post.Id,
      afterComment: this.postState.commentCursor.endCursor
    };

    this.loadMoreCommentsEvent.emit(input);
  }

  deletePost(): void {
    const input: PostInput = {
      postID: this.postState.post.Id
    };
    this.deletePostEvent.emit(input);
  }

  deleteComment(commentId): void {
    const input: CommentPostInput = {
      postId: this.postState.post.Id,
      commentInput: {
        commentID: commentId
      }
    };
    this.deleteCommentEvent.emit(input);
  }

  // post body
  postClicked(event: any): void {
    const target = event.target;
    if (target.tagName !== 'A') {
      this.router.navigate([
        `${this.postState.post.author.handle}/post/${this.postState.post.Id}`
      ]);
    }
  }

  @HostListener('document:click', ['$event'])
  public handleClick(event: Event): void {
    if (event.target instanceof HTMLAnchorElement) {
      const element = event.target as HTMLAnchorElement;
      if (
        element.className === 'mentionlink' ||
        element.className === 'hashtagLink'
      ) {
        event.preventDefault();
        const route = element?.getAttribute('href');
        if (route) {
          this.router.navigate([`/${route}`]);
        }
      }
    }
  }

  postReportClicked(event: any): void {
    // const target = event.target;
    // if (target.tagName !== 'A') {
    //   this.router.navigate([
    //     '',
    //     this.postState.post.echoContent.originalPost?.author?.handle,
    //     'post',
    //     this.postState.post.echoContent.originalPost?.Id
    //   ]);
    // }
  }

  favouritePost(): void {
    this.favouritePostEvent.emit({
      postID: this.postState.post.Id
    });
  }

  unFavouritePost(): void {
    this.unFavouritePostEvent.emit({
      postID: this.postState.post.Id
    });
  }

  unVotePost(): void {
    const input: VotePostInput = {
      postID: this.postState.post.Id
    };
    this.unVotePostEvent.emit(input);
  }

  castForPoll(id: number): void {
    const input: CastPollVoteInput = {
      postID: this.postState.post.Id,
      answerID: id
    };

    this.castForPollEvent.emit(input);
  }

  public apiCallbackFn = (route: string) => {
    return this.http.get(route);
  };

  createReplyComment(commentInput: CommentReplyEventInput): void {
    const input: CreateReplyMessageInput = {
      commentID: commentInput?.commentId,
      postID: this.postState?.post?.Id,
      payload: {
        ...{ images: commentInput.images },
        postID: this.postState?.post?.Id,
        comment: commentInput.comment,
        parentCommentID: commentInput.commentId
      }
    };

    this.createCommentReplyEvent.emit(input);
  }

  hideReplyComments(commentID: string): void {
    this.hideReplyCommentsEvent.emit({
      postID: this.postState?.post?.Id,
      commentID
    });
  }

  loadMoreReplyComments(commentID: string): void {
    this.loadMoreReplyCommentsEvent.emit({
      postID: this.postState?.post?.Id,
      commentID
    });
  }

  upVoteReplyComment(data: PostCommentReplyInput): void {
    const input: PostCommentReplyInput = {
      ...data,
      postID: this.postState?.post?.Id
    };
    this.upVoteReplyCommentEvent.emit(input);
  }

  deleteReplyComment(data: PostCommentReplyInput): void {
    const input: PostCommentReplyInput = {
      ...data,
      postID: this.postState?.post?.Id
    };
    this.deleteReplyCommentEvent.emit(input);
  }

  downVoteReplyComment(data: PostCommentReplyInput): void {
    const input: PostCommentReplyInput = {
      ...data,
      postID: this.postState?.post?.Id
    };
    this.downVoteReplyCommentEvent.emit(input);
  }

  // emoji functions

  addEmoji(event) {
    const emo = event.emoji.native;
    console.error(emo);

    this.emojiEvent.next(emo);

    // this.commentGroup.get('comment').setValue('okay');
    // console.error(this.commentGroup.value);
  }

  selectedGiphy(gif: Giphy): void {
    const comment: CreateCommentMutationInput = {
      comment: ' ',
      postID: this.postState.post.Id,
      images: [gif.images?.downsized_medium?.url]
    };

    this.createCommentEvent.emit(comment);
    this.emojiTrigger.closeMenu(); // close the giphy menu
  }

  ngOnDestroy() {
    this.unSubscribed$.next();
    this.unSubscribed$.complete();
    this._lightbox.close();
  }
}
